import type {
  Subscription,
  SubscriptionInvoice,
  InvoiceItem,
  SubscriptionPhase
} from '@/features/subscriptions/types/subscriptions/subscription.types'
import type { BaseTimelineEvent } from '@shared/components/panels/components/domain-sections/Timeline/types'
import type { SummaryRow } from '@shared/components/panels/components/display/ItemsTableWithSummary'
import type { PaymentDetail } from '@shared/components/panels/types/panel-common.types'
import type { EventData } from '@shared/types/events.types'

export const SUBSCRIPTION_TIMELINE_EVENTS_DATA: BaseTimelineEvent[] = [
  {
    id: 'invoice-sent',
    title: 'Invoice #INV93u4239u4 sent to Customer name',
    timestamp: '9 Oct 2025, 14:56',
  },
  {
    id: 'contract-signed',
    title: 'Customer name signed off the contract',
    timestamp: '9 Oct 2025, 14:56',
  },
  {
    id: 'billing-schedule-created',
    title: 'Billing schedule created',
    timestamp: '30 Sep, 2025, 14:56',
  }
]

export const SUBSCRIPTION_UPCOMING_INVOICE_ITEMS: InvoiceItem[] = [
  {
    description: 'Pro cloud license',
    quantity: 5,
    unitPrice: '$200.00',
    amount: '$1,000.00'
  },
  {
    description: 'API access add-on',
    quantity: 1,
    unitPrice: '$120.00',
    amount: '$120.00'
  }
]

export const SUBSCRIPTION_UPCOMING_INVOICE_SUMMARY_ROWS: SummaryRow[] = [
  {
    label: 'Subtotal',
    value: '$1,120.00'
  },
  {
    label: 'Total excl. tax',
    value: '$1,120.00'
  },
  {
    label: 'Total',
    value: '$1,232.00'
  },
  {
    label: 'Applied balance',
    value: '-'
  },
  {
    label: 'Amount paid',
    value: '$0.00',
    isFinal: true
  },
  {
    label: 'Amount due',
    value: '$1,232.00',
    isFinal: true
  }
]

export const SUBSCRIPTION_INVOICES_DATA: SubscriptionInvoice[] = [
  {
    total: '$2,500.00',
    currency: 'USD',
    status: 'pending_approval',
    invoiceNumber: 'INV-DRAFT',
    billingPeriod: '1 Oct - 31 Oct, 2025',
    sent: '-',
    due: '-',
    finalized: '-'
  },
  {
    total: '$120.00',
    currency: 'USD',
    status: 'draft',
    invoiceNumber: 'INV-DRAFT',
    billingPeriod: '1 Oct - 31 Oct, 2025',
    sent: '-',
    due: '-',
    finalized: '-'
  },
  {
    total: '$1,232.00',
    currency: 'USD',
    status: 'open',
    invoiceNumber: 'INV00068',
    billingPeriod: '1 Oct - 31 Oct, 2025',
    sent: '10 Oct',
    due: '10 Oct',
    finalized: '10 Oct'
  },
  {
    total: '$1,232.00',
    currency: 'USD',
    status: 'paid',
    invoiceNumber: 'INV00069',
    billingPeriod: '1 Nov - 30 Nov, 2025',
    sent: '20 Oct',
    due: '20 Oct',
    finalized: '20 Oct'
  },
  {
    total: '$620.00',
    currency: 'USD',
    status: 'partially_paid',
    invoiceNumber: 'INV00070',
    billingPeriod: '1 Dec - 31 Dec, 2025',
    sent: '20 Nov',
    due: '20 Nov',
    finalized: '20 Nov'
  }
]

export const SUBSCRIPTION_PHASES_DATA: SubscriptionPhase[] = [
  {
    id: '1',
    name: 'Trial phase',
    period: '1 Oct - 31 Oct, 2025',
    items: [
      {
        id: '1-1',
        name: 'Pro cloud license',
        type: 'SaaS',
        repeats: 'Monthly',
        billed: 'In arrears',
        amount: '$200.00'
      },
      {
        id: '1-2',
        name: 'Onboarding fee',
        type: 'Service',
        repeats: 'One time',
        billed: 'Upfront',
        amount: '$500.00'
      },
      {
        id: '1-3',
        name: 'API access add-on',
        type: 'SaaS',
        repeats: 'Monthly',
        billed: 'In arrears',
        amount: '$120.00'
      }
    ]
  },
  {
    id: '2',
    name: 'Standard billing',
    period: '1 Nov - 31 Jan, 2026',
    items: [
      {
        id: '2-1',
        name: 'Pro cloud license',
        type: 'SaaS',
        repeats: 'Monthly',
        billed: 'In arrears',
        amount: '$200.00'
      }
    ]
  }
]

export const SUBSCRIPTION_GENERAL_DETAILS: PaymentDetail[] = [
  {
    label: 'Customer',
    value: 'Lukoil Ltd'
  },
  {
    label: 'Plan',
    value: 'Fresh Renew'
  },
  {
    label: 'Created',
    value: '21 Oct 2025'
  },
  {
    label: 'Current period',
    value: 'Oct 21 to Nov 21'
  },
  {
    label: 'Trial',
    value: 'Yes, 7-days'
  },
  {
    label: 'PO Number',
    value: '-'
  },
  {
    label: 'Invoice memo',
    value: 'Standard markup for EUR → USD conversions applied to EU merchant transactions for cross-border payments.'
  }
]

export const SUBSCRIPTION_EVENTS_DATA: EventData[] = [
  {
    id: 'evt_001',
    eventType: 'subscription.created',
    description: 'Subscription created for Premium Plan',
    timestamp: '15 Nov 2025, 10:23',
    requestId: 'req_abc123def456',
    status: 'succeeded'
  },
  {
    id: 'evt_002',
    eventType: 'subscription.updated',
    description: 'Subscription plan changed from Basic to Premium',
    timestamp: '14 Nov 2025, 16:45',
    requestId: 'req_xyz789ghi012',
    status: 'succeeded'
  },
  {
    id: 'evt_003',
    eventType: 'invoice.payment_succeeded',
    description: 'Payment received for invoice INV00068',
    timestamp: '13 Nov 2025, 09:12',
    requestId: 'req_def456jkl789',
    status: 'succeeded'
  },
  {
    id: 'evt_004',
    eventType: 'subscription.trial_will_end',
    description: 'Trial period ending in 3 days',
    timestamp: '12 Nov 2025, 08:00',
    requestId: 'req_mno345pqr678',
    status: 'pending'
  },
  {
    id: 'evt_005',
    eventType: 'invoice.created',
    description: 'Invoice INV00068 generated for billing period',
    timestamp: '10 Nov 2025, 14:30',
    requestId: 'req_stu901vwx234',
    status: 'succeeded'
  }
]

export const mockSubscriptions: Subscription[] = [
  {
    id: 'sub_001',
    customer: 'Lukoil Ltd',
    customerId: 'cust_001',
    status: 'active',
    plan: 'Fresh Renew',
    planId: 'plan_001',
    product: 'Pro Cloud License',
    avgMonthly: '$200.00 USD/monthly',
    avgYearly: '$2,400.00 USD/yearly',
    created: '2025-10-21'
  },
  {
    id: 'sub_002',
    customer: 'TechNova Inc',
    customerId: 'cust_002',
    status: 'inactive',
    plan: 'Starter Basic',
    planId: 'plan_002',
    product: 'API Gateway',
    avgMonthly: '$49.00 USD/monthly',
    avgYearly: '$588.00 USD/yearly',
    created: '2025-09-15'
  },
  {
    id: 'sub_003',
    customer: 'Green Valley Co',
    customerId: 'cust_003',
    status: 'scheduled',
    plan: 'Enterprise Plus',
    planId: 'plan_003',
    product: 'Full Platform Suite',
    avgMonthly: '$899.00 USD/monthly',
    avgYearly: '$10,788.00 USD/yearly',
    created: '2025-11-01'
  },
  {
    id: 'sub_004',
    customer: 'Meridian Labs',
    customerId: 'cust_004',
    status: 'cancelled',
    plan: 'Growth Plan',
    planId: 'plan_004',
    product: 'Analytics Dashboard',
    avgMonthly: '$120.00 USD/monthly',
    avgYearly: '$1,440.00 USD/yearly',
    created: '2025-08-10'
  }
]
